/**
 * ImportPicker — the inline imports finder: search + tag chips + owner scope +
 * an optional one-search narrowing, over an always-visible result list.
 * Single-select, no navigation. Shared by AI Review and Backchannel Tree.
 *
 * It composes existing Imports-slice pieces rather than forking them: the chip
 * row is a SUBSET of `ALL_TAG_CHIPS` (`tagDefs.ts`), `ImportOwnerMenu` and
 * `SearchFilterChip` are reused verbatim, and the data comes off `useImportsList`
 * — the same hook the Imports page fetches with.
 *
 * INVARIANTS a future editor must not break:
 *
 * 1. NO CLIENT-SIDE DISCARD. Every row the server returns is rendered. Rows this
 *    page cannot operate on are DISABLED WITH THE REASON (`unusable`), never
 *    removed. Filtering after the server has paginated makes both numbers on
 *    screen wrong — the page holds fewer rows than `page_size`, and the pager's
 *    `total` counts rows that cannot be picked. Disabling keeps them honest by
 *    construction.
 * 2. URL STATE IS NOT OWNED HERE. `searchIdThrive` arrives as a prop and clearing
 *    it calls back out. `ImportSelectionBar` owns this screen's query string; two
 *    components writing one URL is how that file's deep-link bugs started.
 * 3. The row's activation control is a REAL `<button>` (single-select) or a REAL
 *    `<input type="checkbox">` (multi-select), and the search anchors are
 *    OUTSIDE it. See `ImportPickerRow` — this is an accessibility requirement,
 *    not a style preference.
 *
 * Why the finder pages at all, and why the scope menu comes from its own
 * endpoint, are recorded in
 * `.agent/Tasks/2026-08-04-shared-import-selection/`; the disable-vs-hide
 * decision and its accepted cost are Decision 1 of
 * `.agent/Tasks/2026-08-11-import-picker-search-context/README.md`.
 */
import type { ImportTag } from "@nu-assist/contracts";
import { Check, Search } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { displayTitle } from "../../../routes/research/import-detail/importTitle";

// `ImportSummary` comes from the CLIENT module, not `@nu-assist/contracts`
// directly — round 2 of Combine & Filter (sub-task R2-B) locally extends it
// with an optional `.compare` (see `imports.client.ts`'s own TODO(r2 merge)
// block). A plain generated row is still assignable here (the field is
// optional), so every OTHER caller of this component is unaffected.
import type { ImportSummary, ImportsClient } from "../../../clients/imports.client";
import { RetryButton } from "../../../components/RetryButton";
import { TablePagination } from "../../../components/TablePagination";
import { ImportOwnerMenu } from "../../../routes/research/imports/ImportOwnerMenu";
import { ImportOwnersError } from "../../../routes/research/imports/ImportOwnersError";
import {
  SearchFilterChip,
  resolveSearchFilterLabel,
} from "../../../routes/research/imports/SearchFilterChip";
import { ALL_TAG_CHIPS } from "../../../routes/research/imports/tagDefs";
import { useImportOwners } from "../../../routes/research/imports/useImportOwners";
import { useImportsList } from "../../../routes/research/imports/useImportsList";
import { ImportSearchLinks } from "./ImportSearchLinks";
import { PickerSearchFilterInput } from "./PickerSearchFilterInput";
import { formatContactCount, formatImportDate } from "./importLabels";
import type { ImportUsability, UnusableImport } from "./requirements";

/** The finder's chip subset — All + Raw Import / Company Map / AI Review / Exported; `enriched`
 *  and `combined` are not in the v38 finder chip row. `combined` joins it ONLY
 *  when a caller opts in via `includeCombinedChip` (Combine & Filter's own
 *  source picker, sub-task 04) — every other host keeps the v38 row as-is. */
const FINDER_TAGS: ReadonlySet<ImportTag | null> = new Set(["raw", "bct", "ai", "exported", null]);
const FINDER_CHIPS = ALL_TAG_CHIPS.filter((chip) => FINDER_TAGS.has(chip.value));
const COMBINED_CHIP = ALL_TAG_CHIPS.find((chip) => chip.value === "combined");

/**
 * The tags the finder drops server-side: the caller's own exclusion, plus
 * `combined` while the Raw chip is on. A combined list carries
 * `['raw', ..., 'combined']`, so the server's `tags && ['raw']` returns it
 * under a chip that says Raw (/pr-review iter 1 of PR #993, Agent C M5 — the
 * Imports page got this fix then; the pickers did not until 2026-10-05).
 */
export function wireExcludeTags(
  callerExcludes: readonly ImportTag[],
  tag: ImportTag | null
): ImportTag[] {
  const out = [...callerExcludes];
  if (tag === "raw" && !out.includes("combined")) out.push("combined");
  return out;
}

const DEFAULT_PAGE_SIZE = 8;
const SEARCH_DEBOUNCE_MS = 300;

export interface ImportPickerProps {
  client: ImportsClient;
  meEmail: string;
  /**
   * Currently selected import id — controlled by the caller. SINGLE-SELECT
   * mode (the original, every existing caller). Ignored when `selectedIds` /
   * `onToggle` are supplied (multi-select mode, below) — a caller passes ONE
   * pair or the other, never both.
   */
  selectedId?: string | null;
  onSelect?: (item: ImportSummary) => void;
  /**
   * MULTI-SELECT mode (additive, sub-task 04 — Combine & Filter's source
   * picker). Supplying this (with `onToggle`) replaces every row's activation
   * button with a native checkbox — the ONLY toggle; the rest of the row is
   * not clickable. `checked` reflects membership in this set. Reaching
   * `maxSelected` disables nothing up front: every row stays pickable, and a
   * toggle that would add one past the cap is refused (see `onMaxReached`).
   * `selectedId` / `onSelect` are ignored while this is set.
   */
  selectedIds?: ReadonlySet<string>;
  /** Required alongside `selectedIds` — toggles one row in or out. */
  onToggle?: (item: ImportSummary) => void;
  /**
   * Multi-select cap — Combine & Filter passes `MAX_COMBINE_SOURCES` (20).
   * Hidden from the recruiter: no "Max N" pill, no disabled rows. Ticking an
   * unselected row while `selectedIds` is already at the cap does NOT call
   * `onToggle`; it calls `onMaxReached`. Unticking is never refused. Ignored
   * in single-select mode. A multi-select host must wire `onMaxReached` (or
   * pass its own `maxSelected`): with the cap hidden, the refusal is otherwise
   * signalled only by the picker's sr-only fallback notice, and a sighted
   * recruiter sees the click do nothing.
   */
  maxSelected?: number;
  /** Multi-select: the recruiter tried to select past `maxSelected` and the
   *  toggle was refused. The host shows its own warning (e.g. a toast, which
   *  is itself announced), so the picker's sr-only notice stays silent then. */
  onMaxReached?: () => void;
  /**
   * Adds the `combined` chip to the finder row (excluded from every other
   * host's chip row — see `FINDER_TAGS`'s own comment). Combine & Filter is
   * the one caller that wants a combined list pickable as a SOURCE for
   * another combine.
   */
  includeCombinedChip?: boolean;
  /**
   * Why this page cannot operate on a row (`null` when it can). Rows that come
   * back with a reason render disabled and SHOW it — they are never hidden, so
   * the page size and the pager total stay honest. Same predicate the caller
   * hands `ImportSelectionBar` for the selected-import notice, so a row and the
   * notice above it can never disagree.
   */
  unusable?: (item: ImportUsability) => UnusableImport | null;
  /**
   * Initial owner scope (`you` | `all` | `<email>`). BCT passes `all` because
   * its old dropdown loaded firm-wide; defaulting it to `you` there would
   * silently hide a colleague's import that a recruiter routinely runs on.
   */
  defaultOwner?: string;
  /** When set, a Cancel button is rendered (finder opened over a selection). */
  onCancel?: () => void;
  /**
   * Narrow to one Thrive search. Never seeded automatically — see
   * `ImportSelectionBar`, which owns the param and the opt-in control.
   */
  searchIdThrive?: string | null;
  /** Display name for the filtered search, when the caller knows it. */
  searchName?: string | null;
  /** Drop the search narrowing. Required for the chip to render a clear button. */
  onClearSearchFilter?: () => void;
  /**
   * Apply (or REPLACE) the search narrowing from inside the finder. Required for
   * the search-filter input to render — without it the only way in was the
   * "Only this search" button outside the picker, so moving from search X to
   * search Y meant clearing and leaving the screen. That button was removed on
   * 2026-08-27 (Trello #729 cl.8), which makes this the finder's ONLY in-app
   * apply path — a caller that omits it leaves a shared link as the sole way to
   * narrow. The bar owns the URL (invariant 2), so this only calls back out.
   */
  onApplySearchFilter?: (searchId: string, searchName: string | null) => void;
  /**
   * Rows per page. Exposed mainly so a test can force a multi-page list
   * against the shared fixture without editing it — the fixture holds fewer
   * rows than the default page size, so at the default no pager would ever
   * render in a unit test and the paging behaviour would go uncovered.
   */
  pageSize?: number;
  /**
   * One lifecycle tag to drop SERVER-SIDE, before pagination. BCT passes
   * `"bct"`: a BCT-derived import cannot seed another tree (product rule,
   * 2026-08-12 — "you can not let them choose a bct map to make an other map
   * off of"), so those rows are not merely disabled, they are not fetched or
   * counted at all — page and pager total stay honest, same as
   * `exclude_status`. AI Review omits it and keeps seeing BCT imports.
   * The matching tag chip is dropped from the chip row: a filter that can
   * only ever produce an empty page is not a filter, it is a dead end.
   *
   * One tag or several. Independently of it, the Raw chip always adds
   * `combined` (see `wireExcludeTags`).
   */
  excludeTag?: ImportTag | readonly ImportTag[] | null;
  /**
   * ADDITIVE (round 2, sub-task R2-B): render extra content next to a row's
   * title — Combine & Filter's `SourcePicker` uses it for a Compare-state
   * chip ("Compared · 3d" / "Comparing…"). `undefined` (every other caller)
   * renders nothing extra; `ImportSelectionBar` and every existing test are
   * unchanged by this prop's mere existence.
   */
  renderRowAdornment?: (item: ImportSummary) => ReactNode;
}

export function ImportPicker({
  client,
  meEmail,
  selectedId = null,
  onSelect,
  selectedIds,
  onToggle,
  maxSelected = 10,
  onMaxReached,
  includeCombinedChip = false,
  unusable,
  defaultOwner = "you",
  onCancel,
  searchIdThrive = null,
  searchName = null,
  onClearSearchFilter,
  onApplySearchFilter,
  pageSize = DEFAULT_PAGE_SIZE,
  excludeTag = null,
  renderRowAdornment,
}: ImportPickerProps) {
  // Multi-select mode is "on" iff the caller supplied BOTH halves — a lone
  // `selectedIds` with no `onToggle` (or vice versa) falls back to
  // single-select rather than toggling silently with nowhere to report to.
  const isMulti = selectedIds !== undefined && onToggle !== undefined;
  // sr-only announcement of a refused toggle at the cap, for a host with no
  // `onMaxReached` of its own (a host toast is announced already; both would
  // read the refusal twice). Cleared as soon as the selection moves again.
  const [capNotice, setCapNotice] = useState("");
  // biome-ignore lint/correctness/useExhaustiveDependencies: clearing on a selection change is the point
  useEffect(() => {
    setCapNotice("");
  }, [selectedIds]);
  function toggleWithCap(item: ImportSummary) {
    if (
      selectedIds !== undefined &&
      !selectedIds.has(item.import_id) &&
      selectedIds.size >= maxSelected
    ) {
      if (onMaxReached) onMaxReached();
      else setCapNotice("You've reached the maximum number of lists. This one was not added.");
      return;
    }
    onToggle?.(item);
  }
  // Widened at INIT as well as in the effect below. As an effect alone, a picker
  // mounted with a narrowing already set (a shared link) rendered once at `you`,
  // fetched, then widened and fetched again — two requests and a visible flip of
  // the scope menu. Same lazy-initializer shape `ImportsPage.tsx` uses.
  const [owner, setOwner] = useState(() =>
    searchIdThrive && defaultOwner === "you" ? "all" : defaultOwner
  );
  const [tag, setTag] = useState<ImportTag | null>(null);
  const [searchText, setSearchText] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  // Whether the `you` → `all` widening below was ours to undo. Seeded true when
  // the initializer above already did it, so a narrowing present at mount is
  // un-widened on clear just like one applied later.
  const widenedByNarrowing = useRef(searchIdThrive !== null && defaultOwner === "you");

  // Debounce the search input into `q`. The page reset lives with the `setQ`
  // that causes it, and is CONDITIONAL on the search actually moving: this
  // effect also fires once on mount, and an unconditional `setPage(1)` there
  // would snap the user back to page 1 if they paged inside the debounce
  // window. `ImportsPage.tsx` carries the same guard for the same reason.
  useEffect(() => {
    const id = setTimeout(() => {
      if (searchText === q) return;
      setQ(searchText);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
    // `q` is in the deps so the guard above can compare against the CURRENT
    // committed search. Cost: when `q` settles, this re-runs and schedules one
    // more timer that immediately early-returns. No loop — `q` cannot change
    // again without `searchText` moving first. Do not "clean up" the dep.
  }, [searchText, q]);

  // Every filter that changes the result set sends the user back to page 1 —
  // otherwise a narrower list leaves them on a page that no longer exists.
  // Mirrors `ImportsPage`'s `changeOwner` / `changeTag`.
  function changeOwner(next: string) {
    // Picking a scope by hand revokes our claim to un-pick it. Without this, the
    // sequence narrow (you→all) → choose "you" by hand → clear the narrowing left
    // the ref still true, so the clear "restored" a scope the recruiter had already
    // overridden — stomping a deliberate choice with a stale inference.
    widenedByNarrowing.current = false;
    setOwner(next);
    setPage(1);
  }

  function changeTag(next: ImportTag | null) {
    setTag(next);
    setPage(1);
  }

  // A search narrowing arrives from outside (the bar's opt-in control), so the
  // page reset it needs cannot live with a local setter. Keyed on the PARAM, not
  // on `owner`, so it fires when the narrowing changes and never fights a scope
  // the recruiter picks afterwards.
  //
  // It also widens `you` → `all`, for the reason `ImportsPage` widens on the
  // same param: the imports for a search are usually somebody else's (a search
  // has a team), so keeping the You scope lands the recruiter on an empty list
  // next to a chip claiming to filter — the worst of both readings. An explicit
  // scope other than `you` is left alone; they chose it.
  //
  // DIVERGENCE from `ImportsListView` (2026-08-14), recorded so it is not read
  // as drift: that component now widens only when a filter ARRIVES (none → one)
  // and never when one search replaces another, because there the widened scope
  // stays on screen and a recruiter who narrows back to Me after it means it.
  // This effect diverges on two halves, and only one of them has a defence.
  // The un-widen on clear fits an overlay whose scope is destroyed on close.
  // The widening on REPLACEMENT does not: apply search A, pick Me, apply
  // search B, and the pick is stomped without this component ever unmounting.
  // It survives because it is simply the behaviour this component had until
  // 2026-08-14 and has not been revisited here — a latent bug, cheaper than
  // its sibling's was, not a design. Bringing the two in line is a change to
  // THIS surface's behaviour and would have to be reasoned against the
  // un-widen it interacts with, so it is deferred rather than dismissed.
  //
  // Deps are exactly `[searchIdThrive]` and biome is satisfied: `setPage` and
  // `setOwner` are stable setters, and the functional `setOwner` update means
  // the current `owner` is read at apply time rather than captured — which is
  // also why this cannot go stale.
  useEffect(() => {
    setPage(1);
    if (searchIdThrive) {
      setOwner((prev) => {
        if (prev !== "you") return prev;
        widenedByNarrowing.current = true;
        return "all";
      });
      return;
    }
    // Un-widen the scope THIS component widened, but only while it is still mounted to
    // do so. The ref is what separates "we widened it" from "the recruiter chose it";
    // `changeOwner` clears the ref, so a hand-picked scope is never "restored" over.
    //
    // The premise is NOT a general symmetry — an earlier version of this comment
    // claimed one and then contradicted itself four lines later. This effect is
    // reachable in exactly TWO paths, because every other route destroys the component
    // before an effect could run:
    //
    //   1. chip-clear with the finder OPEN — the picker is mounted, this effect fires,
    //      and it is the entire mechanism. Also the only path where a stale ref could
    //      stomp a hand-picked scope, which is the bug `changeOwner` closes.
    //   2. a commit whose new selection turns out BLOCKED — `expanded` stays true via
    //      `blockedReason`, so the picker survives that commit and the narrowing
    //      cleared in the same handler does reach a live effect. Obscure but real, and
    //      the reason the flat claim "a commit always collapses" was wrong.
    //
    // Every OTHER collapse unmounts the picker: `ImportSelectionBar` renders it as a
    // bare `{expanded ? <ImportPicker/> : null}` with no `key` and no memo, so `owner`,
    // `tag`, `searchText`, `q`, `page` and this ref are all destroyed, and the next
    // open re-initializes from `defaultOwner`.
    //
    // So the risk here is NOT under-propagation — it is that the effect looks
    // under-propagated and invites a redundant un-widen in `collapse()`, which would be
    // dead code wearing the shape of an invariant. Measured, not inferred: filter →
    // pick a colleague's scope → Cancel → re-open leaves the scope at You and sends
    // `owner=you` on the first request.
    if (widenedByNarrowing.current) {
      widenedByNarrowing.current = false;
      setOwner("you");
    }
  }, [searchIdThrive]);

  // The caller's exclusion as a list, memoized on the prop: the
  // `finderChips` memo below depends on it.
  const callerExcludes = useMemo<readonly ImportTag[]>(
    () => (excludeTag == null ? [] : Array.isArray(excludeTag) ? excludeTag : [excludeTag]),
    [excludeTag]
  );

  const query = useImportsList({
    client,
    owner,
    tag,
    q,
    searchIdThrive,
    // `failed` is dropped SERVER-SIDE, which is not a contradiction of
    // invariant 1 below — that invariant forbids discarding rows the server
    // returned, and this stops them being returned or counted at all, so both
    // numbers on screen stay true. A parse failure differs from every other
    // unusable state: it is terminal AND its reason is stored but surfaced
    // nowhere in the app, so the row teaches the recruiter nothing while
    // occupying a slot they could have used. `parsing` / `uploaded` stay —
    // those become usable in a minute, and the Baby Chops → import → BCT flow
    // lands on a row that is still parsing.
    excludeStatus: "failed",
    // Same server-side placement as `excludeStatus`, same invariant-1
    // compatibility: rows the caller can NEVER use (BCT children in the BCT
    // picker) are not returned or counted, instead of arriving disabled.
    excludeTag: wireExcludeTags(callerExcludes, tag),
    page,
    pageSize,
  });

  // The chip row minus the excluded tag: with `excludeTag` active the server
  // never returns rows carrying it, so its chip could only ever produce an
  // empty page. Note this filters the CHIP ROW, not the result list —
  // invariant 1 (no client-side row discard) is untouched.
  const finderChips = useMemo(() => {
    // Reading order: "All" (value null) always survives; every real tag
    // survives unless it is the excluded one.
    const base = FINDER_CHIPS.filter(
      (chip) => chip.value === null || !callerExcludes.includes(chip.value)
    );
    if (
      !includeCombinedChip ||
      !COMBINED_CHIP?.value ||
      callerExcludes.includes(COMBINED_CHIP.value)
    )
      return base;
    return [...base, COMBINED_CHIP];
  }, [callerExcludes, includeCombinedChip]);

  // Every loaded row is rendered. No `.filter()` — see the file docblock.
  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  // The server total, which now describes exactly what the list shows: nothing
  // is discarded client-side, so the pager can no longer over-count.
  const total = query.data?.total ?? 0;
  // Same clamp `ImportsPage` runs, for the same reason and off the same hook:
  // `useImportsList` polls every 3s while any visible row is still parsing or
  // exporting, so `total` can shrink with no user action and leave the caller
  // past the end. Without it the picker sits on an empty finder until the user
  // notices the pager. Kept in both places rather than shared — two `useState`
  // pages with different sizes and different reset paths, where a shared hook
  // would have to take all of that as arguments to save four lines.
  // `!isFetching`, not `!isPlaceholderData` — see the long-form reasoning on
  // `ImportsPage`'s clamp. Short version: a cache hit returns stale data as
  // REAL data, so a placeholder-only guard still clamps on a stale total and
  // eats the caller's click.
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  useEffect(() => {
    if (query.isSuccess && !query.isFetching && page > pageCount) setPage(pageCount);
  }, [query.isSuccess, query.isFetching, page, pageCount]);
  // The scope options come from their own endpoint, NOT from this page. Every
  // page-derived variant narrows the menu with the very filters it is supposed
  // to sit above — including `owner` itself, which left the picked recruiter as
  // the only remaining option and stranded the user there.
  const { query: ownersQuery, owners } = useImportOwners({ client });

  return (
    <div className="flex flex-col gap-3" data-testid="import-picker">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-60 flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search by people list title or search name"
            aria-label="Search by people list title or search name"
            data-testid="import-picker-search"
            className="w-full rounded-md border border-border bg-surface py-1.5 pl-9 pr-3 text-body text-text placeholder:text-text-muted hover:border-border-hover focus:border-border-hover focus:outline-none"
          />
        </div>
        {onApplySearchFilter ? (
          <PickerSearchFilterInput
            client={client}
            appliedSearchId={searchIdThrive}
            onApply={onApplySearchFilter}
          />
        ) : null}
        <ImportOwnerMenu value={owner} meEmail={meEmail} owners={owners} onChange={changeOwner} />
        <ImportOwnersError query={ownersQuery} />
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            data-testid="import-picker-cancel"
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-label text-text-secondary hover:border-border-hover"
          >
            Cancel
          </button>
        ) : null}
      </div>

      {/* The narrowing is only visible if we say so. Same chip the Imports
          listing renders for the same concept, so "filter by search" has one
          spelling in the app rather than two. */}
      {searchIdThrive && onClearSearchFilter ? (
        <SearchFilterChip
          label={resolveSearchFilterLabel({
            searchId: searchIdThrive,
            nameFromUrl: searchName,
            items,
          })}
          onClear={onClearSearchFilter}
        />
      ) : null}

      <fieldset
        className="flex min-w-0 flex-wrap items-center gap-1.5 border-0 p-0 m-0"
        data-testid="import-picker-chips"
      >
        <legend className="sr-only">Filter by type</legend>
        {finderChips.map((chip) => {
          const active = chip.value === tag;
          return (
            <button
              key={chip.label}
              type="button"
              aria-pressed={active}
              data-chip={chip.value ?? "all"}
              onClick={() => changeTag(chip.value)}
              className={
                active
                  ? "rounded-full border border-accent bg-accent-muted px-3 py-1 text-label text-accent"
                  : "rounded-full border border-border bg-surface px-3 py-1 text-label text-text-secondary hover:border-border-hover"
              }
            >
              {chip.label}
            </button>
          );
        })}
      </fieldset>

      {/* A labelled GROUP, via the same `<fieldset>` + sr-only `<legend>` pattern
          the chip row above uses — so the label is actually exposed without an
          ARIA role and without a suppression. This was a bare `aria-label` on a
          role-less <div>, which assistive technology ignores. Deliberately not
          `listbox`: that role owes its user roving-tabindex focus management this
          component does not implement, and `option` children would be pruned. */}
      <fieldset
        className="flex min-w-0 flex-col gap-1 border-0 p-0 m-0"
        data-testid="import-picker-results"
      >
        <legend className="sr-only">Select a people list</legend>
        {/* Only over real rows — not the error, loading or empty states. */}
        {!query.isError && !query.isLoading && items.length > 0 ? (
          <ImportPickerHeader multi={isMulti} hasAdornment={renderRowAdornment != null} />
        ) : null}
        {query.isError ? (
          <div
            className="flex items-center gap-3 text-body text-error"
            data-testid="import-picker-error"
          >
            <span>Couldn't load people lists.</span>
            {/* Distinct VISIBLE label from the owners-error retry above — the
                two can be on screen together, and identical names are
                ambiguous to sighted and screen-reader users alike
                (`SearchDetailPage.tsx:382-387`). */}
            <RetryButton
              busy={query.isFetching}
              label="Retry people lists"
              busyLabel="Retrying…"
              // The row it sits in is `text-error`; `RetryButton` defaults to
              // `tone="warning"`, which put amber-on-red inside one sentence.
              tone="error"
              onClick={() => void query.refetch()}
            />
          </div>
        ) : query.isLoading ? (
          <p className="text-body text-text-muted">Loading people lists…</p>
        ) : items.length === 0 ? (
          <p className="text-body text-text-muted" data-testid="import-picker-empty">
            No people lists match the current filter / search.
          </p>
        ) : (
          items.map((item) => {
            const isSelected = isMulti
              ? (selectedIds?.has(item.import_id) ?? false)
              : item.import_id === selectedId;
            return (
              <ImportPickerRow
                key={item.import_id}
                item={item}
                isSelected={isSelected}
                reason={unusable?.(item) ?? null}
                onActivate={isMulti ? toggleWithCap : onSelect}
                renderRowAdornment={renderRowAdornment}
                multi={isMulti}
              />
            );
          })
        )}
      </fieldset>

      {isMulti ? (
        <output aria-live="polite" className="sr-only" data-testid="import-picker-cap-notice">
          {capNotice}
        </output>
      ) : null}

      {!query.isError ? (
        <TablePagination
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          noun="people list"
          testId="import-picker-pagination"
        />
      ) : null}
    </div>
  );
}

/**
 * Column headers above the rows — the bare number in each row is the list's
 * contact count, which users could not tell without a label. Mirrors the row's
 * flex layout (`gap-3 px-3`, plus a transparent 1px border for the row's own
 * border) so each label sits over its column. Visual only (`aria-hidden`):
 * every cell already carries its own accessible meaning (the checkbox name,
 * the sr-only count unit), and a header div is not a table header anyway.
 *
 * The adornment and reason-pill columns have no label: both are
 * variable-width and per-row. An empty `shrink-0` stand-in keeps the
 * adornment's `gap-3` so "Searches" lands where chip-less rows start it; the
 * pill sits beside the right-anchored count, which stays aligned regardless.
 */
function ImportPickerHeader({ multi, hasAdornment }: { multi: boolean; hasAdornment: boolean }) {
  return (
    <div
      aria-hidden="true"
      data-testid="import-picker-header"
      className="flex items-center gap-3 border border-transparent px-3 text-caption text-text-muted"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {/* Control spacer: the checkbox box is 16px, the radio dot 12px. */}
        <span className={multi ? "w-4 shrink-0" : "w-3 shrink-0"} />
        <span className="w-14 shrink-0">Date</span>
        <span className="min-w-0 flex-1 truncate">List</span>
      </div>
      {hasAdornment ? <span className="shrink-0" /> : null}
      <span className="min-w-0 flex-1 truncate">Searches</span>
      <span className="w-16 shrink-0 text-right">Contacts</span>
      <span className="w-24 shrink-0 truncate">Owner</span>
    </div>
  );
}

/**
 * One finder row: an activation button, then the linked search, then the counts.
 * (Multi-select swaps the button for a native checkbox beside plain identity
 * text — only the checkbox toggles; see the `multi` branch below. Everything
 * said here about the anchors and `aria-disabled` applies to it unchanged.)
 *
 * The `<button>` is REAL, and holds `data-testid` + `aria-pressed` because it is
 * the selectable control. The search anchors are its SIBLINGS, deliberately
 * outside it. Anything else fails the point of showing them: an anchor inside a
 * `<button>` is invalid HTML, and `role="button"` on a wrapper does not rescue it
 * — `button` is one of the roles with Children Presentational: True (ARIA 1.2
 * §5.2.8), so a conforming screen reader prunes the subtree and the links are
 * never exposed as links at all. `role="option"` fails for the same reason; it is
 * on that same list. A change whose whole purpose is to surface two links cannot
 * hide them from assistive technology, so the links live outside the control and
 * the control stays a plain native button.
 *
 * Consequences of getting this right, all of them subtractions: no ARIA role, no
 * biome suppression, no hand-rolled `onKeyDown` (the native button gives
 * Enter/Space, including not scrolling on Space), no `tabIndex`, and nothing for
 * the anchors to stop propagating to.
 *
 * The wrapper carries the visual box and is NOT interactive. The button spans the
 * row's identity columns (dot · date · filename); the linked search, the reason
 * pill, the count and the owner sit outside it. So the click target is roughly the
 * left half of the row, NOT its full width — which is why the hover affordance is
 * on the button rather than the wrapper. A wrapper-wide highlight advertised the
 * count and owner columns as clickable when they are not.
 *
 * An unusable row stays FOCUSABLE — `aria-disabled` plus a guarded handler, not
 * the `disabled` attribute. `disabled` would drop the control out of the tab
 * order and take the explanation with it, from exactly the users it is for. The
 * reason is given twice: `short` as visible text, and `long` via
 * `aria-describedby` so it is announced on focus rather than only on hover.
 */
function ImportPickerRow({
  item,
  isSelected,
  reason,
  onActivate,
  renderRowAdornment,
  multi = false,
}: {
  item: ImportSummary;
  isSelected: boolean;
  reason: UnusableImport | null;
  /** `onSelect` in single-select mode, `onToggle` in multi-select — the
   *  parent resolves which one applies; this component only ever calls it. */
  onActivate?: (item: ImportSummary) => void;
  /** See `ImportPickerProps.renderRowAdornment`. */
  renderRowAdornment?: (item: ImportSummary) => ReactNode;
  /** Multi-select mode: the row is NOT a control — a native checkbox is the
   *  only toggle (several rows can be on at once). Single-select keeps the
   *  whole-row button with its decorative radio dot and `aria-pressed`. */
  multi?: boolean;
}) {
  const reasonId = `import-picker-reason-${item.import_id}`;

  function activate() {
    if (reason) return;
    onActivate?.(item);
  }

  // Date + filename: the row's identity columns, shared by both modes. In
  // single-select they sit inside the activation button; in multi-select they
  // are plain text beside the checkbox.
  const identity = (
    <>
      <span className="w-14 shrink-0 text-text-secondary">{formatImportDate(item.created_at)}</span>
      {/* `title` carries the STORED name. Stripping the extension makes
          `acme.csv` and `acme.json` render identically, so the full value has
          to stay one hover away (`/pr-review` iter 2-3, Agent C I4b). */}
      <span
        className="min-w-0 flex-1 truncate text-text"
        // NOT `import-picker-row-title`: rows are counted with the PREFIX
        // selector `[data-testid^="import-picker-row-"]`, so that name made
        // every row match twice and the picker's own tests read double.
        data-testid="import-picker-title"
        title={item.filename}
      >
        {displayTitle(item.filename)}
      </span>
    </>
  );

  return (
    <div
      data-testid={`import-picker-rowbox-${item.import_id}`}
      className={`flex items-center gap-3 rounded-md border px-3 py-2 text-body ${
        reason
          ? "border-border bg-surface opacity-70"
          : isSelected
            ? "border-border bg-accent-muted ring-1 ring-inset ring-accent"
            : "border-border bg-surface"
      }`}
    >
      {multi ? (
        // MULTI-SELECT: only the checkbox toggles. The row around it is not a
        // control — no button, no pointer cursor, no hover tint — so a click on
        // the filename or the counts does nothing, and the selected styling on
        // the wrapper above is a visual state only. A NATIVE checkbox, so Tab
        // reaches it and Space toggles it with no hand-rolled key handling; its
        // accessible name is the list's title, its state is `checked`.
        <div
          data-testid={`import-picker-row-${item.import_id}`}
          data-selected={isSelected}
          data-disabled={reason ? "true" : "false"}
          className="flex min-w-0 flex-1 items-center gap-3"
        >
          <span className="relative flex h-4 w-4 shrink-0 items-center justify-center">
            <input
              type="checkbox"
              checked={isSelected}
              // Unusable: focusable but inert, same rationale as the single-select
              // button (see this component's docblock) — `disabled` would drop it
              // out of the tab order and take the announced reason with it. The
              // guard in `activate` keeps the controlled `checked` from moving.
              onChange={activate}
              aria-label={`Select ${displayTitle(item.filename)}`}
              aria-disabled={reason ? true : undefined}
              aria-describedby={reason ? reasonId : undefined}
              data-testid={`import-picker-checkbox-${item.import_id}`}
              className={`h-4 w-4 shrink-0 appearance-none rounded-xs border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                isSelected ? "border-accent bg-accent" : "border-border-hover bg-transparent"
              } ${reason ? "cursor-not-allowed" : "cursor-pointer"}`}
            />
            {isSelected ? (
              <Check
                aria-hidden="true"
                className="pointer-events-none absolute h-3 w-3 text-bg"
                strokeWidth={3}
              />
            ) : null}
          </span>
          {identity}
        </div>
      ) : (
        <button
          type="button"
          aria-pressed={isSelected}
          aria-disabled={reason ? true : undefined}
          aria-describedby={reason ? reasonId : undefined}
          onClick={activate}
          data-testid={`import-picker-row-${item.import_id}`}
          data-selected={isSelected}
          data-disabled={reason ? "true" : "false"}
          // The hover affordance lives on the BUTTON, not the wrapper. On the
          // wrapper it lit up the whole row — including the counts and owner
          // columns, which are outside the click target — so the row advertised
          // itself as clickable in places where clicking did nothing.
          // `rounded-md` to match the wrapper's tier — a bare `rounded` is Tailwind's
          // 4px DEFAULT, not one of the project's tokens, and nesting it inside an
          // 8px `rounded-md` wrapper reads as a rendering bug (SOP 02-tokens.md, radius rename trap).
          //
          // Hover tints the surface rather than underlining: this sits beside two real
          // links, and underline-on-hover is their affordance. Borrowing it made the
          // filename look like a third link that opens something.
          className={`flex min-w-0 flex-1 items-center gap-3 rounded-md text-left focus:outline-none focus:ring-1 focus:ring-inset focus:ring-accent ${
            reason ? "cursor-not-allowed" : "cursor-pointer hover:bg-accent-muted"
          }`}
        >
          <span
            aria-hidden="true"
            className={`h-3 w-3 shrink-0 rounded-full border ${
              isSelected ? "border-accent bg-accent" : "border-border-hover bg-transparent"
            }`}
          />
          {identity}
        </button>
      )}
      {/* Sibling of the button, not a child — same reason the search links and
          reason pill sit outside it (see this component's own docblock): a
          caller's adornment (Combine & Filter's Compare chip) may itself carry
          a focusable `Tooltip` trigger, and a nested focusable element inside
          this row's `<button>` would give it two tab stops for one control. */}
      {renderRowAdornment ? (
        <span className="shrink-0" data-testid={`import-picker-row-adornment-${item.import_id}`}>
          {renderRowAdornment(item)}
        </span>
      ) : null}
      <ImportSearchLinks
        searchIdThrive={item.search_id_thrive ?? null}
        searchName={item.search_name ?? null}
        className="min-w-0 flex-1"
        testIdPrefix={`import-picker-search-${item.import_id}`}
      />
      {/* `failed` and `parsing` are deliberately different messages: one is
          permanent, the other resolves on its own. `long` rides an sr-only span
          wired through `aria-describedby` rather than `title`, which is
          hover-only and so unreachable by keyboard and touch. */}
      {reason ? (
        <span
          id={reasonId}
          data-testid={reasonId}
          // `text-warning-ink`, NOT `text-warning`: amber-as-text washes out on
          // the light surface (~1.6:1, and the row adds `opacity-70` on top).
          // `--warning` is for fills and borders; `--warning-ink` is the deep
          // amber meant for text (~4.4:1). This pill is the only thing telling a
          // recruiter WHY a row cannot be picked, so it has to be readable.
          className="shrink-0 rounded-pill bg-warning-muted px-2 py-0.5 text-caption text-warning-ink"
        >
          <span data-testid={`${reasonId}-short`}>{reason.short}</span>
          <span className="sr-only"> — {reason.long}</span>
        </span>
      ) : null}
      <span
        className="w-16 shrink-0 text-right text-text-secondary"
        data-testid={`import-picker-count-${item.import_id}`}
      >
        {/* The bare number is what sighted users see under the "Contacts"
            column header; the header is `aria-hidden`, so the unit rides an
            sr-only span instead. */}
        <span aria-hidden="true">{item.total_contacts.toLocaleString()}</span>
        <span className="sr-only">{formatContactCount(item.total_contacts)}</span>
      </span>
      <span className="w-24 shrink-0 truncate text-text-secondary">
        {item.owner_name ?? item.uploader_email}
      </span>
    </div>
  );
}
